import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bell, AlertTriangle, Award, ClipboardCheck, TestTube,
  Check, CheckCheck, Filter, ChevronRight, Inbox,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  entityId?: string | null;
}

// ─── Config maps ──────────────────────────────────────────────────────────────

const typeIconMap: Record<string, { icon: typeof Bell; color: string; bg: string }> = {
  cert_expiry_critical:    { icon: Award,           color: '#DC2626', bg: '#FEE2E2' },
  cert_expiry_warning:     { icon: Award,           color: '#D97706', bg: '#FEF3C7' },
  cert_expiry:             { icon: Award,           color: '#D97706', bg: '#FEF3C7' },
  capa_deadline_critical:  { icon: AlertTriangle,   color: '#DC2626', bg: '#FEE2E2' },
  capa_overdue:            { icon: AlertTriangle,   color: '#D97706', bg: '#FEF3C7' },
  capa_deadline:           { icon: AlertTriangle,   color: '#D97706', bg: '#FEF3C7' },
  test_request_submitted:  { icon: TestTube,        color: '#2563EB', bg: '#DBEAFE' },
  audit_finding_critical:  { icon: ClipboardCheck,  color: '#DC2626', bg: '#FEE2E2' },
  audit_due:               { icon: ClipboardCheck,  color: '#b07d1a', bg: '#FEF9C3' },
  workflow_update:         { icon: ClipboardCheck,  color: '#b07d1a', bg: '#FEF9C3' },
  batch_released:          { icon: Bell,            color: '#16A34A', bg: '#DCFCE7' },
  system:                  { icon: Bell,            color: '#6899A0', bg: '#F0FAFA' },
};

const typeRouteMap: Record<string, string> = {
  cert_expiry_critical:    '/certifications',
  cert_expiry_warning:     '/certifications',
  cert_expiry:             '/certifications',
  capa_deadline_critical:  '/capa',
  capa_overdue:            '/capa',
  capa_deadline:           '/capa',
  test_request_submitted:  '/requests',
  audit_finding_critical:  '/audits',
  audit_due:               '/audits',
  workflow_update:         '/workflows',
  batch_released:          '/bmr',
  system:                  '/dashboard',
};

const typeLabelMap: Record<string, string> = {
  cert_expiry_critical:    'Certification',
  cert_expiry_warning:     'Certification',
  cert_expiry:             'Certification',
  capa_deadline_critical:  'CAPA',
  capa_overdue:            'CAPA',
  capa_deadline:           'CAPA',
  test_request_submitted:  'Test Request',
  audit_finding_critical:  'Audit',
  audit_due:               'Audit',
  workflow_update:         'Workflow',
  batch_released:          'Batch',
  system:                  'System',
};

const FILTER_TABS = ['All', 'Unread', 'CAPA', 'Certification', 'Audit', 'Workflow', 'System'] as const;
type FilterTab = typeof FILTER_TABS[number];

// ─── Component ────────────────────────────────────────────────────────────────

export default function Notifications() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<FilterTab>('All');

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: async () => {
      const { data } = await api.get('/notifications');
      return data.data as Notification[];
    },
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.put(`/notifications/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAllRead = useMutation({
    mutationFn: () => api.put('/notifications/mark-all-read'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const notifications = data || [];
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const filtered = useMemo(() => {
    return notifications.filter((n) => {
      if (activeTab === 'All') return true;
      if (activeTab === 'Unread') return !n.isRead;
      const label = typeLabelMap[n.type] || 'System';
      return label === activeTab;
    });
  }, [notifications, activeTab]);

  const handleClick = (notif: Notification) => {
    if (!notif.isRead) markRead.mutate(notif.id);
    const base = typeRouteMap[notif.type] || '/dashboard';
    const route = notif.entityId ? `${base}?openId=${notif.entityId}` : base;
    navigate(route);
  };

  const handleMarkRead = (e: React.MouseEvent, notif: Notification) => {
    e.stopPropagation();
    markRead.mutate(notif.id);
  };

  return (
    <div className="p-6 space-y-5 max-w-3xl mx-auto">
      <PageHeader
        title="Notifications"
        subtitle="Stay on top of alerts, deadlines, and system updates"
        actions={
          unreadCount > 0 ? (
            <button
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
              className="btn-ghost flex items-center gap-2 text-sm disabled:opacity-50"
            >
              <CheckCheck size={14} />
              Mark all read
            </button>
          ) : undefined
        }
      />

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total', value: notifications.length, color: '#374151' },
          { label: 'Unread', value: unreadCount, color: '#DC2626' },
          { label: 'Read', value: notifications.length - unreadCount, color: '#16A34A' },
        ].map((s) => (
          <div key={s.label} className="card p-4 text-center">
            <p className="font-serif font-bold text-2xl" style={{ color: s.color }}>{s.value}</p>
            <p className="text-xs text-text-muted mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 flex-wrap">
        <Filter size={13} className="text-text-dim mr-1" />
        {FILTER_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              activeTab === tab
                ? 'bg-teal-600 text-white border-teal-600'
                : 'bg-card text-text-muted border-border hover:border-teal-400 hover:text-text'
            }`}
          >
            {tab}
            {tab === 'Unread' && unreadCount > 0 && (
              <span className="ml-1.5 px-1.5 py-px text-[10px] font-bold rounded-full bg-red-500 text-white leading-none">
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Notifications list */}
      {isLoading && (
        <div className="card divide-y divide-border/50">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex gap-4 p-4 animate-pulse">
              <div className="w-9 h-9 rounded-full bg-card2 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-card2 rounded w-2/3" />
                <div className="h-2.5 bg-card2 rounded w-full" />
                <div className="h-2 bg-card2 rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <div className="card p-12 text-center">
          <Inbox size={32} className="text-text-dim mx-auto mb-3" />
          <p className="font-semibold text-text">No notifications</p>
          <p className="text-sm text-text-muted mt-1">
            {activeTab === 'Unread' ? "You're all caught up!" : 'Nothing here yet.'}
          </p>
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <div className="card divide-y divide-border/50 overflow-hidden">
          {filtered.map((notif) => {
            const cfg = typeIconMap[notif.type] || typeIconMap.system;
            const Icon = cfg.icon;
            const label = typeLabelMap[notif.type] || 'System';

            return (
              <div
                key={notif.id}
                onClick={() => handleClick(notif)}
                className={`group flex gap-4 p-4 cursor-pointer transition-colors hover:bg-bg ${
                  !notif.isRead ? 'bg-teal/[0.03]' : ''
                }`}
              >
                {/* Unread indicator */}
                <div className="flex-shrink-0 flex items-start pt-2">
                  {!notif.isRead
                    ? <div className="w-2 h-2 rounded-full bg-teal-500" />
                    : <div className="w-2 h-2" />
                  }
                </div>

                {/* Icon */}
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: cfg.bg }}
                >
                  <Icon size={16} style={{ color: cfg.color }} />
                </div>

                {/* Body */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm leading-snug ${notif.isRead ? 'text-text-muted' : 'font-semibold text-text'}`}>
                      {notif.title}
                    </p>
                    <span className="flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full bg-bg border border-border text-text-muted">
                      {label}
                    </span>
                  </div>
                  <p className="text-xs text-text-muted mt-1 leading-relaxed">{notif.message}</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[11px] text-text-dim">
                      {formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true })}
                      <span className="mx-1.5 opacity-40">·</span>
                      {format(new Date(notif.createdAt), 'd MMM yyyy, HH:mm')}
                    </p>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!notif.isRead && (
                        <button
                          onClick={(e) => handleMarkRead(e, notif)}
                          className="text-[11px] text-teal hover:text-teal-dark font-medium flex items-center gap-0.5"
                        >
                          <Check size={11} /> Mark read
                        </button>
                      )}
                      <ChevronRight size={13} className="text-text-dim ml-1" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
